import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/components/ui/card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";
import { useUpload } from "@/hooks/use-upload";
import { expertApplicationSchema, ExpertApplicationData } from "./expert-form/schema";
import { FormHeader } from "./expert-form/FormHeader";
import { PersonalInfoStep } from "./expert-form/PersonalInfoStep";
import { SpecializationsStep } from "./expert-form/SpecializationsStep";
import { ExperienceStep } from "./expert-form/ExperienceStep";
import { PricingStep } from "./expert-form/PricingStep";

export function ExpertApplicationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { sendEmail, isSending } = useTransactionalEmail();
  const { uploadImage, isUploading } = useUpload();

  const form = useForm<ExpertApplicationData>({
    resolver: zodResolver(expertApplicationSchema),
    defaultValues: {
      agreeToTerms: false,
      specializations: [],
      availability: [],
      offeredPackages: [],
      certifications: [],
      packagePricing: {},
      linkedinUrl: "",
      websiteUrl: "",
      phone: "",
      profileImage: "",
      cvFile: "",
    },
  });

  const { handleSubmit, setValue, watch, formState: { errors } } = form;

  // Watch for certification changes from PersonalInfoStep
  const formCertifications = watch("certifications");
  React.useEffect(() => {
    if (formCertifications) {
      setSelectedCertifications(formCertifications);
    }
  }, [formCertifications]);

  // Function to get validation feedback
  const getValidationFeedback = () => {
    const missingFields: string[] = [];
    const formData = form.getValues();

    // Check each mandatory field
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      missingFields.push("Full Name");
    }
    if (!formData.email || !formData.email.includes('@')) {
      missingFields.push("Email Address");
    }
    if (!formData.phone || formData.phone.trim().length === 0) {
      missingFields.push("Phone Number");
    }
    if (!formData.profileImage || formData.profileImage.trim().length === 0) {
      missingFields.push("Profile Image");
    }
    if (!formData.professionalTitle || formData.professionalTitle.trim().length < 2) {
      missingFields.push("Professional Title");
    }
    if (!formData.yearsExperience || formData.yearsExperience < 1) {
      missingFields.push("Years of Experience");
    }
    if (!formData.bio || formData.bio.trim().length < 100) {
      missingFields.push("Professional Bio (minimum 100 characters)");
    }
    if (!formData.specializations || formData.specializations.length === 0) {
      missingFields.push("At least one Area of Specialization");
    }
    if (!formData.availability || formData.availability.length === 0) {
      missingFields.push("Availability");
    }
    if (!formData.certifications || formData.certifications.length === 0) {
      missingFields.push("At least one Professional Certification");
    }
    if (!formData.offeredPackages || formData.offeredPackages.length === 0) {
      missingFields.push("At least one Service Package");
    }
    if (!formData.agreeToTerms) {
      missingFields.push("Terms Agreement");
    }

    // Check package pricing for selected packages
    const packagePricing = formData.packagePricing || {};
    const selectedPackages = formData.offeredPackages || [];
    selectedPackages.forEach(packageId => {
      if (!packagePricing[packageId] || packagePricing[packageId] < 10) {
        missingFields.push(`Pricing for ${packageId === 'introductory' ? 'Introductory Consultation' : 
          packageId === 'standard' ? 'Standard Support Package' : 'Premium Case Management'}`);
      }
    });

    return missingFields;
  };

  // Function to check application status
  const checkApplicationStatus = () => {
    const missingFields = getValidationFeedback();
    if (missingFields.length > 0) {
      setValidationErrors(missingFields);
      toast.error("Please complete all required fields before submitting");
    } else {
      setValidationErrors([]);
      toast.success("All required fields are completed! You can submit your application.");
    }
  };

  const handleFileUpload = async (file: File, type: 'profile' | 'cv') => {
    try {
      console.log(`Starting ${type} upload for file:`, file.name, file.size, file.type);
      // Try expert-uploads bucket first, fallback to public bucket
      let uploadResult = await uploadImage(file, 'expert-uploads');
      if (!uploadResult) {
        console.log('expert-uploads bucket failed, trying public bucket...');
        uploadResult = await uploadImage(file, 'public');
      }
      if (uploadResult) {
        console.log(`${type} upload successful:`, uploadResult);
        if (type === 'profile') {
          setValue('profileImage', uploadResult.url);
          toast.success('Profile image uploaded successfully');
        } else if (type === 'cv') {
          setValue('cvFile', uploadResult.url);
          toast.success('CV uploaded successfully');
        }
      } else {
        console.error(`${type} upload failed - no result returned from either bucket`);
        toast.error(`Failed to upload ${type === 'profile' ? 'profile image' : 'CV'}. Please try again.`);
      }
    } catch (error) {
      console.error(`${type} upload error:`, error);
      toast.error(`Failed to upload ${type === 'profile' ? 'profile image' : 'CV'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const onSubmit = async (data: ExpertApplicationData) => {
    console.log("🚀 Starting expert application submission...");
    
    // Check for validation errors before submission
    const missingFields = getValidationFeedback();
    if (missingFields.length > 0) {
      setValidationErrors(missingFields);
      toast.error("Please complete all required fields before submitting");
      return;
    }

    setIsSubmitting(true);
    setValidationErrors([]); // Clear any previous validation errors
    
    try {
      // Calculate average hourly rate from package pricing for backward compatibility
      const packagePricing = data.packagePricing || {};
      const prices = Object.values(packagePricing).filter(price => price > 0);
      const averageHourlyRate = prices.length > 0 
        ? Math.round(prices.reduce((sum, price) => sum + price, 0) / prices.length)
        : 75; // Default fallback

      const { error: dbError } = await supabase
        .from("expert_applications")
        .insert({
          user_id: null,
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          professional_title: data.professionalTitle,
          years_experience: data.yearsExperience,
          specializations: data.otherSpecialization 
            ? [...data.specializations, data.otherSpecialization]
            : data.specializations,
          bio: data.bio,
          hourly_rate: averageHourlyRate, // Keep for backward compatibility
          profile_image_url: data.profileImage,
          linkedin_url: data.linkedinUrl || null,
          website_url: data.websiteUrl || null,
          certifications: data.certifications,
          cv_file_url: data.cvFile || null,
          availability_hours: JSON.stringify(data.availability),
          preferred_meeting_types: data.offeredPackages,
          package_pricing: JSON.stringify(packagePricing), // Store new package pricing
          admin_notes: data.agreeToTerms ? "Terms agreed to during application" : null,
        });

      if (dbError) {
        console.error("❌ Database insertion error:", dbError);
        throw dbError;
      }

      try {
        await sendEmail({
          type: 'expert_application_confirmation',
          data: {
            firstName: data.fullName.split(' ')[0],
            email: data.email,
            professionalTitle: data.professionalTitle,
            interviewUrl: "https://calendar.app.google/DWCxRw6TYf7PFNuVA"
          }
        });
      } catch (emailError) {
        console.error("❌ Email sending failed:", emailError);
        toast.error("Application submitted but email confirmation failed. Please check your email or contact support.");
      }

      toast.success("Application submitted successfully! We'll review it and get back to you soon.");
      window.location.href = "/apply-expert/thank-you";
      
    } catch (error) {
      console.error("💥 Critical error during application submission:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSpecializationChange = (specialization: string, checked: boolean) => {
    const updated = checked 
      ? [...selectedSpecializations, specialization]
      : selectedSpecializations.filter(s => s !== specialization);
    
    setSelectedSpecializations(updated);
    setValue("specializations", updated);
  };

  const handleAvailabilityChange = (availability: string, checked: boolean) => {
    const updated = checked 
      ? [...selectedAvailability, availability]
      : selectedAvailability.filter(a => a !== availability);
    
    setSelectedAvailability(updated);
    setValue("availability", updated);
  };

  const handlePackageChange = (packageId: string, checked: boolean) => {
    const updated = checked 
      ? [...selectedPackages, packageId]
      : selectedPackages.filter(p => p !== packageId);
    
    setSelectedPackages(updated);
    setValue("offeredPackages", updated);
  };

  const nextStep = () => setCurrentStep(currentStep + 1);
  const prevStep = () => setCurrentStep(currentStep - 1);

  return (
    <TooltipProvider>
      <Card className="max-w-4xl mx-auto">
        <FormHeader currentStep={currentStep} />
        
        <CardContent>
          {/* Validation Error Alert */}
          {validationErrors.length > 0 && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <div className="font-medium mb-2">Please complete the following required fields:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {validationErrors.map((field, index) => (
                    <li key={index}>{field}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {currentStep === 1 && (
              <PersonalInfoStep 
                form={form}
                onNext={nextStep}
                onFileUpload={handleFileUpload}
              />
            )}

            {currentStep === 2 && (
              <SpecializationsStep 
                form={form}
                onNext={nextStep}
                onPrev={prevStep}
                selectedSpecializations={selectedSpecializations}
                onSpecializationChange={handleSpecializationChange}
                selectedAvailability={selectedAvailability}
                onAvailabilityChange={handleAvailabilityChange}
              />
            )}

            {currentStep === 3 && (
              <ExperienceStep 
                form={form}
                onNext={nextStep}
                onPrev={prevStep}
              />
            )}

            {currentStep === 4 && (
              <PricingStep 
                form={form}
                onPrev={prevStep}
                selectedPackages={selectedPackages}
                onPackageChange={handlePackageChange}
                selectedSpecializations={selectedSpecializations}
                selectedCertifications={selectedCertifications}
                isSubmitting={isSubmitting}
                isSending={isSending}
                isUploading={isUploading}
                onCheckStatus={checkApplicationStatus}
              />
            )}
          </form>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
